import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LuMail } from "react-icons/lu";
import { api, ApiError, errMsg, setAccessToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { User } from "../lib/types";
import { Button, ErrorBanner, Input, Label } from "../components/ui";

const RESEND_COOLDOWN_S = 30;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const inviteCode = params.get("invite");
  const urlError = params.get("error");

  // In-flight magic-link emails from the previous deploy still bounce here with
  // ?error=… — surface it inline instead of the old redirect banner.
  useEffect(() => {
    if (urlError) {
      setError("That sign-in link is no longer valid — enter your email to get a code.");
    }
  }, [urlError]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (targetEmail: string) => {
    setError(null);
    setSubmitting(true);
    try {
      await api.post<{ ok: true }>("/auth/otp/request", { email: targetEmail });
      setStep("code");
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      setError(errMsg(err, "Couldn't send the code. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitEmail = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      return setError("That doesn't look like a valid email address.");
    }
    setEmail(trimmed);
    void requestCode(trimmed);
  };

  const finishSignIn = async (accessToken: string) => {
    setAccessToken(accessToken);
    const user = await api.get<User>("/auth/me");
    setSession(accessToken, user);
    if (inviteCode) navigate(`/invite/${inviteCode}`, { replace: true });
    else navigate(user.name ? "/" : "/onboarding", { replace: true });
  };

  const onSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      return setError("Enter the 6-digit code from your email.");
    }
    setSubmitting(true);
    try {
      const { accessToken } = await api.post<{ accessToken: string }>(
        "/auth/otp/verify",
        { email, code },
      );
      await finishSignIn(accessToken);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Network error — check your connection and retry.",
      );
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  const resetToEmail = () => {
    setStep("email");
    setCode("");
    setError(null);
  };

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-gradient-to-br from-brand-50 via-white to-slate-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-brand-600 text-2xl font-bold text-white shadow-lg shadow-brand-600/30">
            F
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">Welcome to Flat</h1>
          <p className="mt-1 text-sm text-slate-500">
            Sign in with a code we email you — no password needed.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {step === "code" ? (
            <form onSubmit={onSubmitCode} className="space-y-4">
              <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                <LuMail className="h-6 w-6" />
              </div>
              <div className="text-center">
                <h2 className="text-base font-semibold text-slate-900">Enter your code</h2>
                <p className="mt-1 text-sm text-slate-600">
                  We sent a 6-digit code to{" "}
                  <span className="font-medium text-slate-900">{email}</span>.
                </p>
              </div>

              <div>
                <Label htmlFor="code">6-digit code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  disabled={submitting}
                  className="text-center text-2xl font-semibold tracking-[0.4em]"
                />
              </div>

              <ErrorBanner message={error} />

              <Button type="submit" loading={submitting} className="w-full">
                Verify &amp; sign in
              </Button>

              <div className="flex items-center justify-between pt-1 text-xs">
                <button
                  type="button"
                  onClick={resetToEmail}
                  className="font-medium text-slate-500 hover:text-slate-700"
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  disabled={cooldown > 0 || submitting}
                  onClick={() => void requestCode(email)}
                  className="font-medium text-brand-600 hover:text-brand-700 disabled:text-slate-400"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={onSubmitEmail} className="space-y-4">
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  placeholder="you@flatmates.example"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <ErrorBanner message={error} />

              <Button type="submit" loading={submitting} className="w-full">
                Send code
              </Button>

              {inviteCode && (
                <p className="pt-1 text-center text-xs text-slate-500">
                  You'll be returned to the invite after signing in.{" "}
                  <Link
                    to={`/invite/${inviteCode}`}
                    className="font-medium text-brand-600 hover:text-brand-700"
                  >
                    View invite
                  </Link>
                </p>
              )}
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          By continuing you agree to share your email with your flatmates.
        </p>
      </div>
    </div>
  );
}
